// ============================================================
// America's Energy Bridge — Bilingual Dictionary (EN / ES)
// Single source of truth. Every user-facing string lives here.
// To edit a label, change BOTH `en` and `es` values together.
// ============================================================

export type Lang = 'en' | 'es'

export const dictionary = {
  // -------- Common / shared --------
  common: {
    en: 'EN',
    es: 'ES',
    bridge: { en: "America's Energy Bridge", es: "America's Energy Bridge" },
    company: { en: 'Sr. Navarro LLC', es: 'Sr. Navarro LLC' },
    languageLabel: { en: 'Language', es: 'Idioma' },
    learnMore: { en: 'Learn more', es: 'Más información' },
    viewProfile: { en: 'View profile', es: 'Ver perfil' },
    backToDirectory: { en: 'Back to directory', es: 'Volver al directorio' },
    requestIntroduction: {
      en: 'Request Introduction',
      es: 'Solicitar Presentación',
    },
    scheduleCall: { en: 'Schedule a Call', es: 'Agendar una Llamada' },
    close: { en: 'Close', es: 'Cerrar' },
    sampleProfile: { en: 'SAMPLE PROFILE', es: 'PERFIL DE MUESTRA' },
    exportReady: { en: 'Export Ready', es: 'Listo para Exportar' },
    spanishSupport: { en: 'ES Support', es: 'Soporte ES' },
    select: { en: 'Select…', es: 'Seleccione…' },
    required: { en: 'Required', es: 'Requerido' },
    yes: { en: 'Yes', es: 'Sí' },
    no: { en: 'No', es: 'No' },
    inProgress: { en: 'In progress', es: 'En proceso' },
    needsInfo: { en: 'Need more information', es: 'Necesito más información' },
    throughPartner: { en: 'Through a partner', es: 'A través de un socio' },
    submitting: { en: 'Submitting…', es: 'Enviando…' },
    optional: { en: 'optional', es: 'opcional' },
    contactForPricing: { en: 'CONTACT FOR PRICING', es: 'CONSULTAR PRECIOS' },
  },

  // -------- Navbar --------
  nav: {
    solution: { en: 'Solution', es: 'Solución' },
    focus: { en: 'Focus', es: 'Enfoque' },
    how: { en: 'How It Works', es: 'Cómo Funciona' },
    network: { en: 'Network', es: 'Red' },
    suppliers: { en: 'Suppliers', es: 'Proveedores' },
    about: { en: 'About', es: 'Acerca de' },
    faq: { en: 'FAQ', es: 'Preguntas' },
    resources: { en: 'Resources', es: 'Recursos' },
    contact: { en: 'Contact', es: 'Contacto' },
    compliance: { en: 'Compliance', es: 'Cumplimiento' },
    requestAccess: {
      en: 'Request Private Access',
      es: 'Solicitar Acceso Privado',
    },
    privateTradeDesk: {
      en: 'Sr. Navarro LLC · Private Trade Desk',
      es: 'Sr. Navarro LLC · Mesa de Comercio Privada',
    },
  },

  // -------- Hero --------
  hero: {
    headlineStart: {
      en: 'Connecting U.S. Energy Suppliers With',
      es: 'Conectando Proveedores de Energía de EE.UU. con',
    },
    headlineEnd: {
      en: 'Venezuelan Industrial Buyers',
      es: 'Compradores Industriales Venezolanos',
    },
    subheadline: {
      en: 'A private B2B bridge for oil, gas, petrochemical, electricity, methane reduction, and industrial infrastructure opportunities between the United States and Venezuela.',
      es: 'Un puente B2B privado para oportunidades en petróleo, gas, petroquímica, electricidad, reducción de metano e infraestructura industrial entre Estados Unidos y Venezuela.',
    },
    ctaSupplier: { en: "I'm a U.S. Supplier", es: 'Soy Proveedor de EE.UU.' },
    ctaBuyer: { en: "I'm a Venezuelan Buyer", es: 'Soy Comprador Venezolano' },
    ctaAccess: { en: 'Request Private Access', es: 'Solicitar Acceso Privado' },
    trustLine: {
      en: 'Built for verified sourcing, industrial procurement, and compliance-aware introductions.',
      es: 'Diseñado para abastecimiento verificado, compras industriales y presentaciones conscientes de cumplimiento.',
    },
    badges: {
      en: [
        'Verified Supplier Network',
        'Spanish / English Deal Support',
        'Compliance-Aware Intake',
        'Industrial Procurement Focus',
      ],
      es: [
        'Red de Proveedores Verificados',
        'Soporte en Español / Inglés',
        'Recepción Consciente de Cumplimiento',
        'Enfoque en Compras Industriales',
      ],
    },
    liveCorridor: {
      en: 'LIVE TRADE CORRIDOR',
      es: 'CORREDOR DE COMERCIO ACTIVO',
    },
  },

  // -------- Problem --------
  problem: {
    eyebrow: { en: 'The Trust Gap', es: 'La Brecha de Confianza' },
    title: {
      en: 'The Opportunity Is Real. The Trust Gap Is Bigger.',
      es: 'La Oportunidad Es Real. La Brecha de Confianza Es Mayor.',
    },
    subtitle: {
      en: 'Venezuelan industrial companies need reliable equipment, parts, technology, and service providers. U.S. suppliers want new opportunities, but face uncertainty around sanctions, payment risk, documentation, language, logistics, and buyer verification.',
      es: 'Las empresas industriales venezolanas necesitan equipos, repuestos, tecnología y proveedores de servicios confiables. Los proveedores estadounidenses buscan nuevas oportunidades, pero enfrentan incertidumbre en sanciones, riesgo de pagos, documentación, idioma, logística y verificación de compradores.',
    },
    suppliersTitle: {
      en: 'For U.S. Suppliers',
      es: 'Para Proveedores de EE.UU.',
    },
    buyersTitle: {
      en: 'For Venezuelan Buyers',
      es: 'Para Compradores Venezolanos',
    },
    suppliers: {
      en: [
        'Hard to verify serious buyers',
        'Sanctions and export control concerns',
        'Payment and logistics risk',
        'Spanish language barriers',
        'Lack of local deal intelligence',
      ],
      es: [
        'Difícil verificar compradores serios',
        'Inquietudes sobre sanciones y control de exportaciones',
        'Riesgo de pagos y logística',
        'Barreras del idioma español',
        'Falta de inteligencia local de negocios',
      ],
    },
    buyers: {
      en: [
        'Hard to find U.S. suppliers willing to engage',
        'Difficult documentation requirements',
        'Need certified industrial equipment',
        'Need fast sourcing for maintenance and operations',
        'Need reliable communication in English and Spanish',
      ],
      es: [
        'Difícil encontrar proveedores estadounidenses dispuestos',
        'Requisitos de documentación complejos',
        'Necesidad de equipos industriales certificados',
        'Necesidad de abastecimiento rápido para operaciones',
        'Necesidad de comunicación confiable en inglés y español',
      ],
    },
  },

  // -------- Solution --------
  solution: {
    eyebrow: { en: 'The Solution', es: 'La Solución' },
    title: {
      en: 'A Verified Bridge Between Demand and Supply',
      es: 'Un Puente Verificado Entre Demanda y Oferta',
    },
    features: {
      en: [
        {
          title: 'Verified Supplier Profiles',
          body: 'Vetted U.S. manufacturers, distributors, and service providers with documented capability and certifications.',
        },
        {
          title: 'Spanish Buyer Intake',
          body: 'Native Spanish intake forms and communication for Venezuelan procurement teams and operators.',
        },
        {
          title: 'RFQ Matching',
          body: 'Structured request-for-quote routing to qualified U.S. suppliers by category and capability.',
        },
        {
          title: 'Certification & Capability Tags',
          body: 'API, ISO, ASME, UL, and OSHA tags surface compliant suppliers for industrial-grade requirements.',
        },
        {
          title: 'Compliance Readiness Checklist',
          body: 'A standardized checklist that prepares both sides for legal, sanctions, and export control review.',
        },
        {
          title: 'Logistics & Documentation Support',
          body: 'Coordination with freight forwarders and customs partners to align documentation early.',
        },
        {
          title: 'Deal Desk Support',
          body: 'Concierge support across the introduction lifecycle — from intake to qualified conversation.',
        },
        {
          title: 'Private Trade Introductions',
          body: 'Curated, opt-in introductions only after both parties pass internal review.',
        },
      ],
      es: [
        {
          title: 'Perfiles de Proveedores Verificados',
          body: 'Fabricantes, distribuidores y proveedores de servicios estadounidenses verificados con capacidad y certificaciones documentadas.',
        },
        {
          title: 'Recepción de Compradores en Español',
          body: 'Formularios y comunicación nativos en español para equipos de compras y operadores venezolanos.',
        },
        {
          title: 'Coincidencia de Solicitudes (RFQ)',
          body: 'Enrutamiento estructurado de solicitudes de cotización a proveedores estadounidenses calificados por categoría y capacidad.',
        },
        {
          title: 'Etiquetas de Certificación y Capacidad',
          body: 'Etiquetas API, ISO, ASME, UL y OSHA destacan proveedores en cumplimiento para requisitos industriales.',
        },
        {
          title: 'Lista de Cumplimiento',
          body: 'Una lista estandarizada que prepara a ambas partes para revisión legal, de sanciones y control de exportaciones.',
        },
        {
          title: 'Soporte Logístico y Documental',
          body: 'Coordinación con agentes de carga y socios aduanales para alinear la documentación desde el inicio.',
        },
        {
          title: 'Soporte de Mesa de Negocios',
          body: 'Soporte concierge a lo largo del ciclo de presentación — desde la recepción hasta la conversación calificada.',
        },
        {
          title: 'Presentaciones Comerciales Privadas',
          body: 'Presentaciones curadas y opcionales sólo después de que ambas partes pasen revisión interna.',
        },
      ],
    },
  },

  // -------- Niche Focus --------
  focus: {
    eyebrow: { en: 'Initial Market Focus', es: 'Enfoque Inicial de Mercado' },
    title: { en: 'Initial Market Focus', es: 'Enfoque Inicial de Mercado' },
    subtitle: {
      en: 'Where the bridge begins. Four anchor categories chosen for both the urgency of demand inside Venezuela and the maturity of U.S. supplier capability.',
      es: 'Donde comienza el puente. Cuatro categorías ancla elegidas por la urgencia de la demanda en Venezuela y la madurez de la capacidad de los proveedores estadounidenses.',
    },
    examplesLabel: { en: 'Examples:', es: 'Ejemplos:' },
    cards: {
      en: [
        {
          title: 'Oilfield Parts and Replacement Equipment',
          examples:
            'Valves, pumps, seals, gaskets, hoses, compressors, fittings, manifolds, wellhead parts, refinery maintenance components.',
        },
        {
          title: 'Energy and Electrical Infrastructure',
          examples:
            'Generators, transformers, industrial controls, automation, storage systems, power distribution equipment, safety systems.',
        },
        {
          title: 'Compliance Ready U.S. Suppliers',
          examples:
            'API, ISO, export documentation, quality systems, industrial certifications, traceability, warranty support.',
        },
        {
          title: 'Methane Reduction and Environmental Technology',
          examples:
            'Leak detection, methane monitoring, emissions reduction, environmental services, safety and monitoring systems.',
        },
      ],
      es: [
        {
          title: 'Repuestos y Equipos para Yacimientos',
          examples:
            'Válvulas, bombas, sellos, empaques, mangueras, compresores, conexiones, múltiples, partes de cabezal, componentes de mantenimiento de refinería.',
        },
        {
          title: 'Infraestructura Energética y Eléctrica',
          examples:
            'Generadores, transformadores, controles industriales, automatización, sistemas de almacenamiento, equipos de distribución de energía, sistemas de seguridad.',
        },
        {
          title: 'Proveedores de EE.UU. en Cumplimiento',
          examples:
            'API, ISO, documentación de exportación, sistemas de calidad, certificaciones industriales, trazabilidad, soporte de garantía.',
        },
        {
          title: 'Reducción de Metano y Tecnología Ambiental',
          examples:
            'Detección de fugas, monitoreo de metano, reducción de emisiones, servicios ambientales, sistemas de seguridad y monitoreo.',
        },
      ],
    },
    categoryLabel: { en: 'CATEGORY', es: 'CATEGORÍA' },
    exploreSuppliers: {
      en: 'Explore suppliers in this category',
      es: 'Explorar proveedores en esta categoría',
    },
  },

  // -------- How It Works --------
  how: {
    eyebrow: { en: 'The Process', es: 'El Proceso' },
    title: { en: 'How It Works', es: 'Cómo Funciona' },
    subtitle: {
      en: 'Three coordinated tracks. One private trade desk.',
      es: 'Tres vías coordinadas. Una mesa de comercio privada.',
    },
    step: { en: 'STEP', es: 'PASO' },
    tracks: {
      buyer: {
        label: { en: 'Venezuelan Buyers', es: 'Compradores Venezolanos' },
        sublabel: {
          en: 'Compradores Venezolanos',
          es: 'Equipos de Compras y Operadores',
        },
        steps: {
          en: [
            'Submit request in Spanish',
            'Share product needs, timeline, budget, end use, and company verification',
            'Receive matched U.S. supplier options',
            'Begin private introduction and documentation review',
          ],
          es: [
            'Envíe su solicitud en español',
            'Comparta necesidades de producto, plazo, presupuesto, uso final y verificación de la empresa',
            'Reciba opciones de proveedores estadounidenses coincidentes',
            'Inicie la presentación privada y revisión de documentación',
          ],
        },
      },
      supplier: {
        label: { en: 'U.S. Suppliers', es: 'Proveedores de EE.UU.' },
        sublabel: {
          en: 'Manufacturers · Distributors · Services',
          es: 'Fabricantes · Distribuidores · Servicios',
        },
        steps: {
          en: [
            'Apply for supplier profile',
            'Add products, certifications, export capability, and categories',
            'Get matched with qualified Venezuelan RFQs',
            'Review opportunities before engaging',
          ],
          es: [
            'Aplique para un perfil de proveedor',
            'Agregue productos, certificaciones, capacidad de exportación y categorías',
            'Reciba coincidencias con solicitudes venezolanas calificadas',
            'Revise oportunidades antes de comprometerse',
          ],
        },
      },
      desk: {
        label: {
          en: 'Sr. Navarro LLC Trade Desk',
          es: 'Mesa de Comercio Sr. Navarro LLC',
        },
        sublabel: {
          en: 'Concierge Coordination',
          es: 'Coordinación Concierge',
        },
        steps: {
          en: [
            'Validate buyer and supplier fit',
            'Organize communication',
            'Coordinate documentation checklist',
            'Support next steps with outside legal, sanctions, export, banking, customs, and logistics professionals where needed',
          ],
          es: [
            'Validar la coincidencia entre comprador y proveedor',
            'Organizar la comunicación',
            'Coordinar la lista de documentación',
            'Apoyar los siguientes pasos con profesionales externos legales, de sanciones, exportación, banca, aduanas y logística según sea necesario',
          ],
        },
      },
    },
  },

  // -------- Supplier Directory --------
  directory: {
    eyebrow: { en: 'Network Preview', es: 'Vista Previa de la Red' },
    title: {
      en: 'Supplier Network Under Development',
      es: 'Red de Proveedores en Desarrollo',
    },
    subtitle: {
      en: 'A preview of database categories and sample internal records. The companies shown below are placeholder profiles to illustrate the directory structure — they are not yet partners.',
      es: 'Una vista previa de las categorías y registros internos de muestra. Las empresas mostradas son perfiles de referencia para ilustrar la estructura del directorio — aún no son socios.',
    },
    searchPlaceholder: {
      en: 'Search categories, states, or capabilities…',
      es: 'Buscar categorías, estados o capacidades…',
    },
    sampleProfilesCount: { en: 'SAMPLE PROFILES', es: 'PERFILES DE MUESTRA' },
    sampleProfileSingular: { en: 'SAMPLE PROFILE', es: 'PERFIL DE MUESTRA' },
    filters: {
      en: [
        'Category',
        'State',
        'Certifications',
        'Export Ready',
        'Spanish Support',
        'Product Type',
        'API Related',
        'Methane Tech',
        'Electrical Infrastructure',
      ],
      es: [
        'Categoría',
        'Estado',
        'Certificaciones',
        'Listo para Exportar',
        'Soporte en Español',
        'Tipo de Producto',
        'Relacionado con API',
        'Tecnología de Metano',
        'Infraestructura Eléctrica',
      ],
    },
    clearFilters: { en: 'Clear filters', es: 'Limpiar filtros' },
    noResults: {
      en: 'No suppliers match your filters.',
      es: 'Ningún proveedor coincide con sus filtros.',
    },
    fields: {
      category: { en: 'Category', es: 'Categoría' },
      capabilities: { en: 'Capabilities', es: 'Capacidades' },
      certifications: { en: 'Certifications', es: 'Certificaciones' },
    },
    statuses: {
      'Pending Verification': {
        en: 'Pending Verification',
        es: 'Pendiente de Verificación',
      },
      Verified: { en: 'Verified', es: 'Verificado' },
      'Invite Only': { en: 'Invite Only', es: 'Solo por Invitación' },
      Rejected: { en: 'Rejected', es: 'Rechazado' },
    },
    illustrativeNote: {
      en: "All listings shown are illustrative placeholder records. No supplier is presented as a confirmed partner of America's Energy Bridge or Sr. Navarro LLC.",
      es: "Todos los listados son registros de referencia. Ningún proveedor se presenta como socio confirmado de America's Energy Bridge o Sr. Navarro LLC.",
    },
  },

  // -------- Supplier Detail Page --------
  supplierDetail: {
    notFound: { en: 'Supplier not found', es: 'Proveedor no encontrado' },
    overview: { en: 'Overview', es: 'Resumen' },
    productsServices: {
      en: 'Products & Services',
      es: 'Productos y Servicios',
    },
    certifications: { en: 'Certifications', es: 'Certificaciones' },
    capability: { en: 'Capability Profile', es: 'Perfil de Capacidad' },
    sourceDirectory: { en: 'Source Directory', es: 'Directorio de Origen' },
    location: { en: 'Location', es: 'Ubicación' },
    category: { en: 'Category', es: 'Categoría' },
    subCategory: { en: 'Sub-category', es: 'Subcategoría' },
    apiRelated: { en: 'API related', es: 'Relacionado con API' },
    isoCertified: { en: 'ISO certified', es: 'Certificado ISO' },
    exportReady: { en: 'Export ready', es: 'Listo para exportar' },
    latamExperience: {
      en: 'Latin America experience',
      es: 'Experiencia en América Latina',
    },
    spanishSupport: { en: 'Spanish support', es: 'Soporte en español' },
    relationshipStatus: { en: 'Relationship status', es: 'Estado de relación' },
    requestNote: {
      en: 'Want to learn more about this supplier or explore a sourcing match? Submit a private request.',
      es: '¿Desea conocer más sobre este proveedor o explorar una coincidencia de abastecimiento? Envíe una solicitud privada.',
    },
  },

  // -------- Build Strategy --------
  strategy: {
    eyebrow: { en: 'Network Build Plan', es: 'Plan de Construcción de la Red' },
    title: {
      en: 'How We Build the First 100 Supplier Network',
      es: 'Cómo Construimos la Primera Red de 100 Proveedores',
    },
    subtitle: {
      en: 'A methodical sourcing process across trusted industrial directories, association lists, and direct outreach.',
      es: 'Un proceso metódico de abastecimiento a través de directorios industriales confiables, listas de asociaciones y contacto directo.',
    },
    liveProgress: { en: 'Live Progress', es: 'Progreso en Vivo' },
    networkInit: {
      en: '0 / 100 SUPPLIERS · NETWORK INITIALIZATION',
      es: '0 / 100 PROVEEDORES · INICIALIZACIÓN DE RED',
    },
    sourcesTitle: { en: 'Sources', es: 'Fuentes' },
    categoriesTitle: {
      en: 'Supplier Categories — First 100',
      es: 'Categorías de Proveedores — Primeros 100',
    },
    metrics: {
      target: { en: 'Target', es: 'Meta' },
      verified: { en: 'Verified', es: 'Verificados' },
      inResearch: { en: 'In Research', es: 'En Investigación' },
      contacted: { en: 'Contacted', es: 'Contactados' },
      interested: { en: 'Interested', es: 'Interesados' },
      approved: { en: 'Approved', es: 'Aprobados' },
    },
  },

  // -------- Buyer Intake Form --------
  buyerForm: {
    eyebrow: { en: 'Private Request', es: 'Solicitud Privada' },
    title: {
      en: 'Industrial Sourcing Request',
      es: 'Solicitud de Compra o Abastecimiento Industrial',
    },
    subtitle: {
      en: 'Complete this form to help us understand the products, equipment, or services your company in Venezuela needs.',
      es: 'Complete este formulario para ayudarnos a entender qué productos, equipos o servicios necesita su empresa en Venezuela.',
    },
    submit: { en: 'Submit Private Request', es: 'Enviar solicitud privada' },
    successTitle: { en: 'Thank you.', es: 'Gracias.' },
    successBody: {
      en: 'Our team will review your request and, if there is a possible match with U.S. suppliers, we will contact you for next steps.',
      es: 'Nuestro equipo revisará la solicitud y, si existe una posible coincidencia con proveedores estadounidenses, le contactaremos para los próximos pasos.',
    },
    consent: {
      en: "I understand that America's Energy Bridge by Sr. Navarro LLC is not a law firm, sanctions advisor, bank, customs broker, or financial broker. All transactions are subject to applicable legal, sanctions, export, customs, banking, and compliance review.",
      es: "Entiendo que America's Energy Bridge by Sr. Navarro LLC no es una firma legal, asesor de sanciones, banco, agente aduanal ni broker financiero. Toda transacción está sujeta a revisión legal, de sanciones, exportación, aduanas, banca y cumplimiento aplicable.",
    },
    consentRequired: {
      en: 'You must accept the terms.',
      es: 'Debe aceptar los términos.',
    },
    fileUpload: {
      en: 'Attach files (placeholder)',
      es: 'Adjuntar archivos (placeholder)',
    },
    fields: {
      companyName: { en: 'Company name', es: 'Nombre de la empresa' },
      taxId: { en: 'Tax ID / RIF', es: 'RIF o identificación fiscal' },
      contact: { en: 'Contact person', es: 'Persona de contacto' },
      title: { en: 'Title', es: 'Cargo' },
      email: { en: 'Email', es: 'Correo electrónico' },
      whatsapp: { en: 'WhatsApp', es: 'WhatsApp' },
      cityState: {
        en: 'City and state in Venezuela',
        es: 'Ciudad y estado en Venezuela',
      },
      sector: { en: 'Industry sector', es: 'Sector de la empresa' },
      pdvsa: {
        en: 'Is your company directly or indirectly related to PDVSA, a government entity, or a state-owned enterprise?',
        es: '¿Su empresa está relacionada directa o indirectamente con PDVSA, una entidad gubernamental, o una empresa estatal?',
      },
      needs: {
        en: 'What do you need to buy or quote?',
        es: '¿Qué necesita comprar o cotizar?',
      },
      productCategory: {
        en: 'Product / service category',
        es: 'Categoría del producto o servicio',
      },
      quantity: { en: 'Approximate quantity', es: 'Cantidad aproximada' },
      budget: { en: 'Estimated budget', es: 'Presupuesto estimado' },
      urgency: { en: 'Urgency', es: 'Urgencia' },
      importExperience: {
        en: 'Do you have permission or experience importing from the U.S.?',
        es: '¿Tiene permiso o experiencia importando desde Estados Unidos?',
      },
      docs: {
        en: 'Do you have technical specifications, photos, drawings, or documents?',
        es: '¿Tiene especificaciones técnicas, fotos, planos o documentos?',
      },
      endUser: {
        en: 'Who will be the end user of the equipment or service?',
        es: '¿Quién será el usuario final del equipo o servicio?',
      },
      comments: { en: 'Additional comments', es: 'Comentarios adicionales' },
    },
    sectorOptions: {
      en: [
        'Oil',
        'Gas',
        'Petrochemical',
        'Electricity',
        'Infrastructure',
        'Industrial maintenance',
        'Transport and logistics',
        'Other',
      ],
      es: [
        'Petróleo',
        'Gas',
        'Petroquímica',
        'Electricidad',
        'Infraestructura',
        'Mantenimiento industrial',
        'Transporte y logística',
        'Otro',
      ],
    },
    pdvsaOptions: {
      en: ['Yes', 'No', "I'm not sure", 'I prefer to explain on a call'],
      es: ['Sí', 'No', 'No estoy seguro', 'Prefiero explicarlo en una llamada'],
    },
    productCategoryOptions: {
      en: [
        'Oilfield parts',
        'Valves and flow control',
        'Pumps and compressors',
        'Generators and electricity',
        'Transformers',
        'Industrial automation',
        'Safety equipment',
        'Environmental tech / methane reduction',
        'Maintenance services',
        'Logistics or transport',
        'Other',
      ],
      es: [
        'Repuestos petroleros',
        'Válvulas y control de flujo',
        'Bombas y compresores',
        'Generadores y electricidad',
        'Transformadores',
        'Automatización industrial',
        'Equipos de seguridad',
        'Tecnología ambiental o reducción de metano',
        'Servicios de mantenimiento',
        'Logística o transporte',
        'Otro',
      ],
    },
    urgencyOptions: {
      en: [
        'Immediate',
        '1 to 2 weeks',
        '30 days',
        '60 to 90 days',
        'Exploring options',
      ],
      es: [
        'Inmediata',
        '1 a 2 semanas',
        '30 días',
        '60 a 90 días',
        'Explorando opciones',
      ],
    },
    importOptions: {
      en: ['Yes', 'No', 'In progress', 'I need guidance'],
      es: ['Sí', 'No', 'En proceso', 'Necesito orientación'],
    },
  },

  // -------- Supplier Application Form --------
  supplierForm: {
    eyebrow: { en: 'Supplier Network', es: 'Red de Proveedores' },
    title: {
      en: 'Apply to Join the U.S. Supplier Network',
      es: 'Aplique para Unirse a la Red de Proveedores de EE.UU.',
    },
    subtitle: {
      en: 'Submit your company for private review. All applications are evaluated against our internal verification checklist.',
      es: 'Envíe su empresa para revisión privada. Todas las aplicaciones se evalúan contra nuestra lista de verificación interna.',
    },
    submit: {
      en: 'Apply for Private Review',
      es: 'Aplicar para Revisión Privada',
    },
    successTitle: { en: 'Application Received', es: 'Aplicación Recibida' },
    successBody: {
      en: 'Thank you. Our team will review your application and reach out if your capabilities match an active or upcoming opportunity.',
      es: 'Gracias. Nuestro equipo revisará su aplicación y le contactará si sus capacidades coinciden con una oportunidad activa o próxima.',
    },
    consent: {
      en: "I understand that America's Energy Bridge by Sr. Navarro LLC is not a law firm, customs broker, bank, or sanctions advisor. Any Venezuela-related transaction may require independent legal, sanctions, export control, customs, banking, and logistics review.",
      es: "Entiendo que America's Energy Bridge by Sr. Navarro LLC no es una firma legal, agente aduanal, banco ni asesor de sanciones. Cualquier transacción relacionada con Venezuela puede requerir revisión independiente legal, de sanciones, control de exportaciones, aduanas, banca y logística.",
    },
    consentRequired: {
      en: 'You must accept the terms.',
      es: 'Debe aceptar los términos.',
    },
    catalogUpload: {
      en: 'Attach catalog (placeholder)',
      es: 'Adjuntar catálogo (placeholder)',
    },
    fields: {
      companyName: { en: 'Company name', es: 'Nombre de la empresa' },
      website: { en: 'Website', es: 'Sitio web' },
      contact: { en: 'Contact person', es: 'Persona de contacto' },
      email: { en: 'Email', es: 'Correo electrónico' },
      phone: { en: 'Phone', es: 'Teléfono' },
      hqState: { en: 'Headquarters state', es: 'Estado de la sede' },
      category: {
        en: 'Product / service category',
        es: 'Categoría del producto o servicio',
      },
      productDescription: {
        en: 'Product description',
        es: 'Descripción del producto',
      },
      certifications: { en: 'Certifications', es: 'Certificaciones' },
      exportsInternationally: {
        en: 'Do you currently export internationally?',
        es: '¿Exporta actualmente a nivel internacional?',
      },
      soldLatam: {
        en: 'Have you sold into Latin America before?',
        es: '¿Ha vendido en América Latina antes?',
      },
      spanishSupport: {
        en: 'Do you have Spanish-speaking sales support?',
        es: '¿Tiene soporte de ventas en español?',
      },
      openToVenezuela: {
        en: 'Open to Venezuelan opportunities subject to legal and compliance review?',
        es: '¿Abierto a oportunidades venezolanas sujetas a revisión legal y de cumplimiento?',
      },
      companyType: {
        en: 'Are you a manufacturer, distributor, service provider, or logistics company?',
        es: '¿Es fabricante, distribuidor, proveedor de servicios o empresa logística?',
      },
      catalog: {
        en: 'Upload product catalog',
        es: 'Cargar catálogo de productos',
      },
      notes: { en: 'Notes', es: 'Notas' },
    },
    certOptions: {
      en: [
        'API',
        'ISO 9001',
        'ISO 14001',
        'ASME',
        'UL',
        'OSHA related',
        'Export documentation available',
        'Other',
      ],
      es: [
        'API',
        'ISO 9001',
        'ISO 14001',
        'ASME',
        'UL',
        'Relacionado con OSHA',
        'Documentación de exportación disponible',
        'Otro',
      ],
    },
    companyTypes: {
      en: [
        'Manufacturer',
        'Distributor',
        'Service Provider',
        'Logistics Company',
      ],
      es: [
        'Fabricante',
        'Distribuidor',
        'Proveedor de Servicios',
        'Empresa de Logística',
      ],
    },
  },

  // -------- Compliance --------
  compliance: {
    notice: { en: 'Compliance Notice', es: 'Aviso de Cumplimiento' },
    body: {
      en: "America's Energy Bridge by Sr. Navarro LLC provides supplier discovery, buyer intake, communication support, and trade opportunity organization. We do not provide legal advice, sanctions advice, customs brokerage, financial services, or guaranteed transaction approval. Any transaction involving Venezuela, PDVSA, government entities, blocked persons, restricted goods, dual-use goods, or regulated sectors must be reviewed by qualified legal, sanctions, export control, banking, customs, and logistics professionals.",
      es: "America's Energy Bridge by Sr. Navarro LLC proporciona descubrimiento de proveedores, recepción de compradores, soporte de comunicación y organización de oportunidades comerciales. No proporcionamos asesoría legal, asesoría de sanciones, agencia aduanal, servicios financieros ni aprobación garantizada de transacciones. Cualquier transacción que involucre a Venezuela, PDVSA, entidades gubernamentales, personas bloqueadas, bienes restringidos, bienes de doble uso o sectores regulados debe ser revisada por profesionales calificados en materia legal, sanciones, control de exportaciones, banca, aduanas y logística.",
    },
  },

  // -------- Business Model --------
  model: {
    eyebrow: { en: 'Roadmap', es: 'Hoja de Ruta' },
    title: {
      en: 'Built as a Private Trade Desk First. Marketplace Second.',
      es: 'Construido como Mesa de Comercio Privada Primero. Marketplace Después.',
    },
    subtitle: {
      en: 'A staged rollout. Trust, verification, and compliance scaffolding come before scale.',
      es: 'Despliegue por fases. La confianza, verificación y andamiaje de cumplimiento van antes que la escala.',
    },
    phase: { en: 'Phase', es: 'Fase' },
    phases: {
      en: [
        {
          title: 'Concierge MVP',
          items: [
            'Landing page',
            'Supplier database',
            'Buyer intake',
            'Manual matching',
            'Private introductions',
          ],
        },
        {
          title: 'Verified Network',
          items: [
            'Supplier profiles',
            'RFQ system',
            'Buyer verification',
            'Category search',
            'Spanish/English deal support',
          ],
        },
        {
          title: 'Trade Desk',
          items: [
            'Sourcing packages',
            'Documentation coordination',
            'Logistics partner referrals',
            'Paid introductions',
            'Opportunity reports',
          ],
        },
        {
          title: 'Marketplace and Distribution',
          items: [
            'Private marketplace',
            'Preferred supplier network',
            'Sr. Navarro LLC sourcing arm',
            'Potential distributor or procurement partner role, subject to legal and compliance structure',
          ],
        },
      ],
      es: [
        {
          title: 'MVP Concierge',
          items: [
            'Página principal',
            'Base de datos de proveedores',
            'Recepción de compradores',
            'Coincidencia manual',
            'Presentaciones privadas',
          ],
        },
        {
          title: 'Red Verificada',
          items: [
            'Perfiles de proveedores',
            'Sistema RFQ',
            'Verificación de compradores',
            'Búsqueda por categoría',
            'Soporte de negocios en español/inglés',
          ],
        },
        {
          title: 'Mesa de Comercio',
          items: [
            'Paquetes de abastecimiento',
            'Coordinación de documentación',
            'Referencias de socios logísticos',
            'Presentaciones pagadas',
            'Reportes de oportunidades',
          ],
        },
        {
          title: 'Marketplace y Distribución',
          items: [
            'Marketplace privado',
            'Red preferida de proveedores',
            'Brazo de abastecimiento Sr. Navarro LLC',
            'Posible rol de distribuidor o socio de compras, sujeto a estructura legal y de cumplimiento',
          ],
        },
      ],
    },
  },

  // -------- Revenue Model --------
  revenue: {
    eyebrow: { en: 'Revenue Model', es: 'Modelo de Ingresos' },
    title: {
      en: 'How the Bridge Sustains Itself',
      es: 'Cómo se Sustenta el Puente',
    },
    subtitle: {
      en: 'A diversified set of revenue streams aligned to verified supplier discovery, buyer intake, and concierge trade desk services.',
      es: 'Un conjunto diversificado de fuentes de ingresos alineado con descubrimiento verificado de proveedores, recepción de compradores y servicios de mesa de comercio concierge.',
    },
    items: {
      en: [
        {
          title: 'Supplier listing fee',
          body: 'Annual or monthly listing in the verified directory.',
        },
        {
          title: 'Premium supplier profile',
          body: 'Enhanced placement, additional capability fields, and priority matching.',
        },
        {
          title: 'Qualified buyer introduction fee',
          body: 'Per-introduction fee charged once both sides clear internal review.',
        },
        {
          title: 'RFQ access fee',
          body: 'Access to qualified Venezuelan RFQs filtered by capability and category.',
        },
        {
          title: 'Sourcing support package',
          body: 'Hands-on sourcing for specific equipment or services on behalf of a buyer.',
        },
        {
          title: 'Translation and documentation support',
          body: 'Bilingual document preparation, translation, and quote formatting.',
        },
        {
          title: 'Trade desk retainer',
          body: 'Ongoing concierge engagement across multiple opportunities.',
        },
        {
          title: 'Market intelligence reports',
          body: 'Sector briefings on Venezuelan industrial demand and U.S. supplier landscape.',
        },
      ],
      es: [
        {
          title: 'Cuota de listado de proveedor',
          body: 'Listado anual o mensual en el directorio verificado.',
        },
        {
          title: 'Perfil de proveedor premium',
          body: 'Posicionamiento mejorado, campos adicionales y coincidencia prioritaria.',
        },
        {
          title: 'Cuota de presentación calificada',
          body: 'Cuota por presentación cobrada una vez que ambas partes pasan la revisión interna.',
        },
        {
          title: 'Cuota de acceso a RFQ',
          body: 'Acceso a RFQs venezolanos calificados, filtrados por capacidad y categoría.',
        },
        {
          title: 'Paquete de soporte de abastecimiento',
          body: 'Abastecimiento práctico de equipos o servicios específicos en nombre del comprador.',
        },
        {
          title: 'Soporte de traducción y documentación',
          body: 'Preparación bilingüe de documentos, traducción y formato de cotizaciones.',
        },
        {
          title: 'Retainer de mesa de comercio',
          body: 'Compromiso concierge continuo a través de múltiples oportunidades.',
        },
        {
          title: 'Reportes de inteligencia de mercado',
          body: 'Resúmenes sectoriales sobre demanda industrial venezolana y panorama de proveedores estadounidenses.',
        },
      ],
    },
  },

  // -------- Founder --------
  founder: {
    eyebrow: { en: 'The Founders', es: 'Los Fundadores' },
    title: {
      en: 'Built by Sr. Navarro LLC',
      es: 'Construido por Sr. Navarro LLC',
    },
    body: {
      en: "America's Energy Bridge was created to help rebuild trusted commercial pathways between U.S. industrial suppliers and Venezuelan companies seeking reliable equipment, services, and sourcing support.",
      es: "America's Energy Bridge fue creado para ayudar a reconstruir vías comerciales confiables entre proveedores industriales estadounidenses y empresas venezolanas que buscan equipos, servicios y soporte de abastecimiento confiables.",
    },
    corridor: {
      en: 'Sr. Navarro LLC · Houston · Caracas Corridor',
      es: 'Sr. Navarro LLC · Corredor Houston · Caracas',
    },
    moreAbout: { en: 'More about us', es: 'Más sobre nosotros' },
  },

  // -------- Final CTA --------
  finalCta: {
    eyebrow: { en: 'Get Involved', es: 'Involúcrate' },
    title: {
      en: 'Join the Private Energy Bridge',
      es: 'Únase al Puente Energético Privado',
    },
    privateCall: {
      en: 'Request a Private Call',
      es: 'Solicitar una Llamada Privada',
    },
  },

  // -------- Footer --------
  footer: {
    tagline: {
      en: 'Private B2B supplier discovery and trade opportunity platform.',
      es: 'Plataforma privada B2B de descubrimiento de proveedores y oportunidades comerciales.',
    },
    disclaimer: {
      en: 'Subject to applicable U.S. sanctions, export controls, banking, customs, and legal review. We help verified parties identify opportunities and prepare for compliant commercial conversations.',
      es: 'Sujeto a sanciones de EE.UU., controles de exportación, banca, aduanas y revisión legal aplicables. Ayudamos a partes verificadas a identificar oportunidades y prepararse para conversaciones comerciales en cumplimiento.',
    },
    navigate: { en: 'Navigate', es: 'Navegar' },
    office: { en: 'Office', es: 'Oficina' },
    languages: { en: 'Languages', es: 'Idiomas' },
    rights: {
      en: 'All rights reserved.',
      es: 'Todos los derechos reservados.',
    },
    internal: { en: 'Internal', es: 'Interno' },
    links: {
      home: { en: 'Home', es: 'Inicio' },
      supplierApplication: {
        en: 'Supplier Application',
        es: 'Aplicación de Proveedor',
      },
      buyerIntake: { en: 'Buyer Intake', es: 'Solicitud de Comprador' },
      compliance: { en: 'Compliance Notice', es: 'Aviso de Cumplimiento' },
      contact: { en: 'Contact', es: 'Contacto' },
      privacy: { en: 'Privacy Policy', es: 'Política de Privacidad' },
      terms: { en: 'Terms', es: 'Términos' },
      about: { en: 'About', es: 'Acerca de' },
      faq: { en: 'FAQ', es: 'Preguntas Frecuentes' },
      resources: { en: 'Resources', es: 'Recursos' },
      suppliers: { en: 'Supplier Directory', es: 'Directorio de Proveedores' },
    },
  },

  // -------- About Page --------
  about: {
    eyebrow: { en: 'About', es: 'Acerca de' },
    title: {
      en: 'A Private Trade Desk for a Complex Corridor',
      es: 'Una Mesa de Comercio Privada para un Corredor Complejo',
    },
    intro: {
      en: "America's Energy Bridge is the operational arm of Sr. Navarro LLC, focused on rebuilding qualified commercial pathways between U.S. industrial suppliers and Venezuelan operators in oil, gas, petrochemical, electricity, methane reduction, and infrastructure sectors.",
      es: "America's Energy Bridge es el brazo operativo de Sr. Navarro LLC, enfocado en reconstruir vías comerciales calificadas entre proveedores industriales estadounidenses y operadores venezolanos en sectores de petróleo, gas, petroquímica, electricidad, reducción de metano e infraestructura.",
    },
    missionTitle: { en: 'Our Mission', es: 'Nuestra Misión' },
    missionBody: {
      en: 'To enable verified, compliance-aware introductions and reduce friction in cross-border industrial procurement — without overstepping into regulated services.',
      es: 'Habilitar presentaciones verificadas y conscientes de cumplimiento, y reducir la fricción en compras industriales transfronterizas — sin sobrepasar a servicios regulados.',
    },
    valuesTitle: { en: 'How We Operate', es: 'Cómo Operamos' },
    values: {
      en: [
        {
          title: 'Verification First',
          body: 'No introduction is made without internal review of both buyer and supplier.',
        },
        {
          title: 'Compliance Awareness',
          body: 'We coordinate with qualified outside professionals on legal, sanctions, export, customs, and banking review.',
        },
        {
          title: 'Bilingual by Design',
          body: 'Every document, conversation, and form is prepared with English and Spanish parity.',
        },
        {
          title: 'Concierge Operations',
          body: 'We start small, do it right, and scale only when the trust scaffolding is in place.',
        },
      ],
      es: [
        {
          title: 'Verificación Primero',
          body: 'No se realiza presentación sin revisión interna del comprador y del proveedor.',
        },
        {
          title: 'Conciencia de Cumplimiento',
          body: 'Coordinamos con profesionales externos calificados en revisión legal, de sanciones, exportación, aduanas y banca.',
        },
        {
          title: 'Bilingüe por Diseño',
          body: 'Cada documento, conversación y formulario se prepara con paridad en inglés y español.',
        },
        {
          title: 'Operaciones Concierge',
          body: 'Empezamos pequeño, lo hacemos bien, y escalamos solo cuando el andamiaje de confianza está en su lugar.',
        },
      ],
    },
  },

  // -------- FAQ Page --------
  faq: {
    eyebrow: { en: 'Frequently Asked Questions', es: 'Preguntas Frecuentes' },
    title: { en: 'Common Questions', es: 'Preguntas Comunes' },
    subtitle: {
      en: 'Concise answers to the most common questions from suppliers, buyers, and partners.',
      es: 'Respuestas concisas a las preguntas más comunes de proveedores, compradores y socios.',
    },
    items: {
      en: [
        {
          q: "Is America's Energy Bridge a marketplace?",
          a: 'No. It is a private B2B trade desk. Public marketplace functionality is a future phase, only after verification scaffolding is fully in place.',
        },
        {
          q: 'Are you a sanctions advisor or law firm?',
          a: 'No. We are not a law firm, sanctions advisor, customs broker, bank, or financial broker. Any Venezuela-related transaction is subject to independent legal, sanctions, export, customs, banking, and logistics review by qualified outside professionals.',
        },
        {
          q: 'How are suppliers verified?',
          a: 'Through an internal verification checklist covering company website, certifications, export readiness, contact information, willingness to pursue Venezuela-related opportunities subject to legal review, and supporting documentation.',
        },
        {
          q: 'Who can submit a buyer request?',
          a: 'Verified Venezuelan industrial companies, operators, and procurement teams in oil, gas, petrochemical, electricity, infrastructure, and industrial maintenance.',
        },
        {
          q: 'Do you guarantee a transaction?',
          a: 'No. We help verified parties identify opportunities and prepare for compliant commercial conversations. We do not guarantee transactions, payments, or regulatory approvals.',
        },
        {
          q: 'Is there a fee?',
          a: 'Public-facing fees are not listed. Pricing is shared during private review and depends on the engagement type — listing, premium profile, qualified introduction, sourcing support, or trade desk retainer.',
        },
        {
          q: 'In what languages do you operate?',
          a: 'English and Spanish. Every form, document, and conversation is prepared with bilingual parity.',
        },
        {
          q: 'How long does verification take?',
          a: 'Typical internal review takes 1–2 weeks depending on complexity, documentation availability, and outside-professional coordination if required.',
        },
      ],
      es: [
        {
          q: "¿Es America's Energy Bridge un marketplace?",
          a: 'No. Es una mesa de comercio B2B privada. La funcionalidad pública de marketplace es una fase futura, sólo después de que el andamiaje de verificación esté completamente en su lugar.',
        },
        {
          q: '¿Son asesores de sanciones o firma legal?',
          a: 'No. No somos firma legal, asesor de sanciones, agente aduanal, banco ni broker financiero. Cualquier transacción relacionada con Venezuela está sujeta a revisión independiente legal, de sanciones, exportación, aduanas, banca y logística por profesionales externos calificados.',
        },
        {
          q: '¿Cómo se verifican los proveedores?',
          a: 'A través de una lista de verificación interna que cubre sitio web de la empresa, certificaciones, capacidad de exportación, información de contacto, disposición para perseguir oportunidades relacionadas con Venezuela sujetas a revisión legal, y documentación de soporte.',
        },
        {
          q: '¿Quién puede enviar una solicitud de comprador?',
          a: 'Empresas industriales venezolanas verificadas, operadores y equipos de compras en petróleo, gas, petroquímica, electricidad, infraestructura y mantenimiento industrial.',
        },
        {
          q: '¿Garantizan una transacción?',
          a: 'No. Ayudamos a partes verificadas a identificar oportunidades y prepararse para conversaciones comerciales en cumplimiento. No garantizamos transacciones, pagos ni aprobaciones regulatorias.',
        },
        {
          q: '¿Hay alguna tarifa?',
          a: 'Las tarifas públicas no se listan. El precio se comparte durante revisión privada y depende del tipo de compromiso — listado, perfil premium, presentación calificada, soporte de abastecimiento o retainer de mesa de comercio.',
        },
        {
          q: '¿En qué idiomas operan?',
          a: 'Inglés y español. Cada formulario, documento y conversación se prepara con paridad bilingüe.',
        },
        {
          q: '¿Cuánto tarda la verificación?',
          a: 'La revisión interna típica toma 1 a 2 semanas dependiendo de la complejidad, disponibilidad de documentación y coordinación con profesionales externos si es necesario.',
        },
      ],
    },
  },

  // -------- Resources Page --------
  resources: {
    eyebrow: { en: 'Resources', es: 'Recursos' },
    title: {
      en: 'Compliance & Reference Resources',
      es: 'Recursos de Cumplimiento y Referencia',
    },
    subtitle: {
      en: 'External directories and authoritative references frequently used during verification, sourcing, and compliance review. These links are provided for convenience — they are not endorsements.',
      es: 'Directorios externos y referencias autorizadas usadas frecuentemente durante verificación, abastecimiento y revisión de cumplimiento. Estos enlaces se proporcionan por conveniencia — no son avales.',
    },
    visit: { en: 'Visit', es: 'Visitar' },
    categories: {
      directories: {
        en: 'Industrial Directories',
        es: 'Directorios Industriales',
      },
      compliance: {
        en: 'Compliance & Sanctions',
        es: 'Cumplimiento y Sanciones',
      },
      certifications: {
        en: 'Certifications & Standards',
        es: 'Certificaciones y Estándares',
      },
    },
  },

  // -------- Contact Page --------
  contact: {
    eyebrow: { en: 'Contact', es: 'Contacto' },
    title: { en: 'Get in Touch', es: 'Póngase en Contacto' },
    subtitle: {
      en: 'For private inquiries, partnership questions, or to schedule a confidential call.',
      es: 'Para consultas privadas, preguntas de alianzas o para agendar una llamada confidencial.',
    },
    nameLabel: { en: 'Name', es: 'Nombre' },
    emailLabel: { en: 'Email', es: 'Correo electrónico' },
    companyLabel: { en: 'Company', es: 'Empresa' },
    roleLabel: { en: 'I am a', es: 'Soy un(a)' },
    roleOptions: {
      en: [
        'U.S. Supplier',
        'Venezuelan Buyer',
        'Logistics Partner',
        'Investor',
        'Press',
        'Other',
      ],
      es: [
        'Proveedor de EE.UU.',
        'Comprador Venezolano',
        'Socio Logístico',
        'Inversionista',
        'Prensa',
        'Otro',
      ],
    },
    messageLabel: { en: 'Message', es: 'Mensaje' },
    submit: { en: 'Send Message', es: 'Enviar Mensaje' },
    successTitle: { en: 'Message sent.', es: 'Mensaje enviado.' },
    successBody: {
      en: 'Thank you. We will respond within 1–2 business days.',
      es: 'Gracias. Responderemos en 1 a 2 días hábiles.',
    },
    directLine: { en: 'Direct Line', es: 'Línea Directa' },
    languages: { en: 'Languages', es: 'Idiomas' },
    hours: { en: 'Hours', es: 'Horario' },
    hoursValue: {
      en: 'Mon–Fri · 8:00 – 18:00 CT',
      es: 'Lun–Vie · 8:00 – 18:00 CT',
    },
  },

  // -------- Schedule Call Modal --------
  schedule: {
    title: { en: 'Schedule a Private Call', es: 'Agendar una Llamada Privada' },
    subtitle: {
      en: 'Select a preferred window and we will confirm a time over email. Calls are confidential and conducted in English or Spanish.',
      es: 'Seleccione una ventana preferida y confirmaremos un horario por correo. Las llamadas son confidenciales y se realizan en inglés o español.',
    },
    nameLabel: { en: 'Full name', es: 'Nombre completo' },
    emailLabel: { en: 'Email', es: 'Correo electrónico' },
    companyLabel: { en: 'Company', es: 'Empresa' },
    windowLabel: { en: 'Preferred window', es: 'Ventana preferida' },
    languageLabel: { en: 'Preferred language', es: 'Idioma preferido' },
    topicLabel: { en: 'Topic', es: 'Tema' },
    submit: { en: 'Request Call', es: 'Solicitar Llamada' },
    successTitle: { en: 'Request received.', es: 'Solicitud recibida.' },
    successBody: {
      en: 'You will receive a confirmation email shortly with available times.',
      es: 'Recibirá un correo de confirmación en breve con horarios disponibles.',
    },
  },

  // -------- 404 --------
  notFound: {
    title: { en: 'Page not found', es: 'Página no encontrada' },
    body: {
      en: 'The page you are looking for does not exist or has moved.',
      es: 'La página que busca no existe o ha sido movida.',
    },
    home: { en: 'Return home', es: 'Volver al inicio' },
  },
} as const
